import { useMemo } from "react";
import { FadeIn } from "~/components/FadeIn";
import { LoadingSpinner } from "~/components/LoadingSpinner";
import { MEASURE_SLUGS } from "~/consts";
import { useApiQuery } from "~/modules/filters/components/useApiQuery";
import {
    AggregateFunctions,
    AggregateQueryInterval,
    ApiQueryEndpoints,
    FilterOperators,
    SortDirections,
} from "~/modules/filters/types";
import {
    AssetTimeseriesColumns,
    AssetTimeseriesGroupBy,
} from "~/modules/asset_timeseries_v4/types";
import {
    ChartTypes,
    TimeSeriesItem,
    TimeSeriesPoint,
    TimeSeriesUnits,
    TimeSeriesView,
} from "~/modules/token_timeseries/types";
import { YAxisTypes } from "~/modules/visualization/components/Timeseries/types";
import { RWA_COLORS } from "~/styles/colors";
import { AssetScreenerListingDualAxisChart } from "./AssetScreenerListingDualAxisChart";
import { formatSinceInceptionPeriod } from "./functions";

// // // //

const CHART_HEIGHT = 320;
const CHART_LOADING_BAR_COUNT = 20;
const CHART_LOADING_LEGEND_COUNT = 3;

const NAV_INCOME_SERIES_COLORS = {
    nav: RWA_COLORS.slate["800"],
    distributions: RWA_COLORS.slate["400"],
    apy: RWA_COLORS.blue["600"],
};

type NavIncomeSeriesView = TimeSeriesView & { yAxis?: 0 | 1 };

interface NavIncomeChartConfig {
    series: NavIncomeSeriesView[];
    hasApy: boolean;
    periodStart: string | null;
    periodEnd: string | null;
    hasData: boolean;
}

interface Props {
    assetID: number;
    assetName: string;
}

function hasUsablePoints(
    item: TimeSeriesItem | undefined,
): item is TimeSeriesItem {
    return (item?.points.length ?? 0) >= 2;
}

/** APY timeseries values are percent (e.g. 5.08); listing chart formatters expect decimal. */
function normalizeApyPoints(points: TimeSeriesPoint[]): TimeSeriesPoint[] {
    return points.map(([date, value]) => [date, Number(value) / 100]);
}

function pickApySeries(items: TimeSeriesItem[]): TimeSeriesItem | undefined {
    const apy30Day = items.find(
        (item) => item.measure.slug === MEASURE_SLUGS.apy_30_day,
    );

    const apy7Day = items.find(
        (item) => item.measure.slug === MEASURE_SLUGS.apy_7_day,
    );

    if (hasUsablePoints(apy30Day)) {
        return apy30Day;
    }

    if (hasUsablePoints(apy7Day)) {
        return apy7Day;
    }

    return apy30Day ?? apy7Day;
}

function buildNavIncomeChartConfig(
    items: TimeSeriesItem[],
): NavIncomeChartConfig {
    const navData = items.find(
        (item) => item.measure.slug === MEASURE_SLUGS.net_asset_value_dollar,
    );
    const distributionData = items.find(
        (item) =>
            item.measure.slug === MEASURE_SLUGS.daily_yield_distributed_dollar,
    );
    const apyData = pickApySeries(items);

    const series: NavIncomeSeriesView[] = [];

    if (hasUsablePoints(navData)) {
        series.push({
            item: {
                ...navData,
                measure: {
                    ...navData.measure,
                    unit: TimeSeriesUnits.dollar,
                },
                group: {
                    ...navData.group,
                    name: "NAV",
                    color: NAV_INCOME_SERIES_COLORS.nav,
                },
            },
            enabled: true,
            chartType: ChartTypes.line,
            yAxis: 0,
        });
    }

    if (hasUsablePoints(distributionData)) {
        series.push({
            item: {
                ...distributionData,
                measure: {
                    ...distributionData.measure,
                    unit: TimeSeriesUnits.dollar,
                },
                group: {
                    ...distributionData.group,
                    name: "Daily distributions",
                    color: NAV_INCOME_SERIES_COLORS.distributions,
                },
            },
            enabled: true,
            chartType: ChartTypes.column,
            yAxis: 0,
        });
    }

    const hasApy = hasUsablePoints(apyData);

    if (hasApy && apyData) {
        const apyLabel =
            apyData.measure.slug === MEASURE_SLUGS.apy_7_day
                ? "7D APY"
                : "30D APY";

        series.push({
            item: {
                ...apyData,
                points: normalizeApyPoints(apyData.points),
                measure: {
                    ...apyData.measure,
                    unit: TimeSeriesUnits.percent,
                },
                group: {
                    ...apyData.group,
                    name: apyLabel,
                    color: NAV_INCOME_SERIES_COLORS.apy,
                },
            },
            enabled: true,
            chartType: ChartTypes.line,
            yAxis: 1,
        });
    }

    const periodSource = navData ?? apyData ?? distributionData;
    const periodStart = periodSource?.points[0]?.[0] ?? null;
    const periodEnd =
        periodSource?.points[periodSource.points.length - 1]?.[0] ?? null;

    return {
        series,
        hasApy,
        periodStart,
        periodEnd,
        hasData: series.length > 0,
    };
}

function useNavIncomeChartData(assetID: number): {
    chartItems: TimeSeriesItem[];
    isLoading: boolean;
} {
    const navIncomeReq = useApiQuery({
        endpoint: ApiQueryEndpoints.AssetTimeseriesV4,
        query: {
            aggregate: {
                aggregateFunction: AggregateFunctions.Sum,
                groupBy: AssetTimeseriesGroupBy.measure,
                interval: AggregateQueryInterval.day,
                groupLimit: 4,
            },
            filter: {
                operator: FilterOperators.And,
                filters: [
                    {
                        field: AssetTimeseriesColumns.assetID,
                        operator: FilterOperators.Equals,
                        value: assetID,
                    },
                    {
                        operator: FilterOperators.Or,
                        filters: [
                            {
                                field: AssetTimeseriesColumns.measureSlug,
                                operator: FilterOperators.Equals,
                                value: MEASURE_SLUGS.net_asset_value_dollar,
                            },
                            {
                                field: AssetTimeseriesColumns.measureSlug,
                                operator: FilterOperators.Equals,
                                value: MEASURE_SLUGS.daily_yield_distributed_dollar,
                            },
                            {
                                field: AssetTimeseriesColumns.measureSlug,
                                operator: FilterOperators.Equals,
                                value: MEASURE_SLUGS.apy_30_day,
                            },
                            {
                                field: AssetTimeseriesColumns.measureSlug,
                                operator: FilterOperators.Equals,
                                value: MEASURE_SLUGS.apy_7_day,
                            },
                        ],
                    },
                ],
            },
            sort: {
                field: AssetTimeseriesColumns.date,
                direction: SortDirections.asc,
            },
            pagination: {
                page: 1,
                perPage: 800,
            },
        },
    });

    const chartItems = ((navIncomeReq.data as { results?: TimeSeriesItem[] })
        ?.results || []) as TimeSeriesItem[];

    return {
        chartItems,
        isLoading: navIncomeReq.isLoading,
    };
}

/**
 * Placeholder layout that mirrors the loaded chart height and legend row.
 */
function NavIncomeChartLoadingState() {
    return (
        <FadeIn duration={200}>
            <div className="space-y-2" aria-busy="true" aria-live="polite">
                <div className="relative">
                    <div
                        className="flex items-end gap-1.5 rounded-md border border-muted/50 bg-muted/15 px-4 pb-6 pt-4"
                        style={{ height: CHART_HEIGHT }}
                        aria-hidden="true"
                    >
                        {Array.from({ length: CHART_LOADING_BAR_COUNT }).map(
                            (_, index) => (
                                <div
                                    key={index}
                                    className="w-full animate-pulse rounded-t-sm bg-muted/55"
                                    style={{
                                        height: `${24 + ((index * 11) % 58)}%`,
                                    }}
                                />
                            ),
                        )}
                    </div>

                    <div className="absolute inset-0 flex items-center justify-center">
                        <LoadingSpinner size={8} />
                    </div>
                </div>

                <div className="flex justify-center gap-5" aria-hidden="true">
                    {Array.from({ length: CHART_LOADING_LEGEND_COUNT }).map(
                        (_, index) => (
                            <div
                                key={index}
                                className="flex items-center gap-1.5"
                            >
                                <div className="h-0.5 w-4 animate-pulse rounded bg-muted/60" />
                                <div className="h-3 w-14 animate-pulse rounded bg-muted/45" />
                            </div>
                        ),
                    )}
                </div>

                <div
                    className="h-3 w-44 animate-pulse rounded bg-muted/40"
                    aria-hidden="true"
                />

                <span className="sr-only">Loading NAV and yield chart</span>
            </div>
        </FadeIn>
    );
}

/**
 * NAV appreciation and income (distributions / APY) over time for the asset screener.
 */
export function NavAndIncomeChart(props: Props) {
    const { assetID, assetName } = props;
    const { chartItems, isLoading } = useNavIncomeChartData(assetID);

    const chartConfig = useMemo(
        () => buildNavIncomeChartConfig(chartItems),
        [chartItems],
    );

    const sinceInceptionPeriod = formatSinceInceptionPeriod(
        chartConfig.periodStart,
        chartConfig.periodEnd,
    );

    return (
        <div>
            {isLoading && <NavIncomeChartLoadingState />}

            {!isLoading && !chartConfig.hasData && (
                <p className="text-sm text-tertiary-foreground">
                    No NAV or yield history available for {assetName}.
                </p>
            )}

            {!isLoading && chartConfig.hasData && (
                <div className="space-y-2">
                    <AssetScreenerListingDualAxisChart
                        series={chartConfig.series}
                        isDualAxis
                        height={CHART_HEIGHT}
                        currencyValueDecimals={4}
                        yAxis={{
                            primary: {
                                min: undefined,
                                enabled: true,
                                type: YAxisTypes.linear,
                                title: "",
                                unit: TimeSeriesUnits.dollar,
                            },
                            secondary: {
                                min: undefined,
                                enabled: chartConfig.hasApy,
                                type: YAxisTypes.linear,
                                title: "",
                                unit: TimeSeriesUnits.percent,
                                opposite: true,
                            },
                        }}
                        legend={{
                            enabled: true,
                            itemWidth: 120,
                            align: "center",
                            verticalAlign: "bottom",
                        }}
                        navigator={{
                            enabled: false,
                            active: false,
                        }}
                    />

                    {sinceInceptionPeriod && (
                        <p className="text-xs text-tertiary-foreground">
                            {sinceInceptionPeriod}
                        </p>
                    )}
                </div>
            )}
        </div>
    );
}
